import React, { Dispatch, SetStateAction } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Search from 'components/search';
import Filter from 'components/filter';
import ExportBtn from 'components/modals/export/exportBtn';
import Pagination from 'components/pagination';
import Table from 'components/table';
import { IFilterConfig } from 'components/filter/types';
import { useOrgLevel } from 'utils/hooks/useOrgLevel';
import { useStoreName } from 'utils/hooks/useStoreName';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import usePayment from 'state/payment/hooks/usePayment';
import { PAYMENTS } from 'state/payment/type';
import routeConfig from 'routes/config';
import { createTipDetailsColumns } from './components/TipDetailsColumns';
import useTipDetails from './hooks/useTipDetails';

interface TipsDetailsProps {
  dateFrom: string;
  dateTo: string;
  firstAmount: string | undefined;
  secondAmount: string | undefined;
  amountOption: string;
  paymentTypeArray: string[];
  storeIdArray: string[];
  searchString: string | undefined;
  filterConfig: IFilterConfig[];
  onExport: () => void;
  activeTab: string;
  queryParams: string;
  setActiveTab: Dispatch<SetStateAction<string>>;
}

const TipsDetails = ({
  dateFrom,
  dateTo,
  firstAmount,
  secondAmount,
  amountOption,
  paymentTypeArray,
  storeIdArray,
  searchString,
  filterConfig,
  onExport,
  activeTab,
  queryParams,
  setActiveTab,
}: TipsDetailsProps): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organisation } = useOrganisation();
  const { settings } = organisation;
  const { payment } = usePayment();
  const isOrgLevel = useOrgLevel();
  const { getStoreName } = useStoreName();

  const { prevKey, nextKey, numResults, prevPage, nextPage } = useTipDetails({
    activeTab,
    dateFrom,
    dateTo,
    firstAmount,
    secondAmount,
    amountOption,
    paymentTypeArray,
    storeIdArray,
    searchString,
    setActiveTab,
  });

  const tableColumns = createTipDetailsColumns({
    timezone: settings?.timezone ?? '',
    isOrgLevel,
    getStoreName,
  });

  const handleRowClick = (row: PAYMENTS.ITipsResult) => {
    navigate(
      `../${routeConfig.PAYMENTS.transactions.path}/${row.transaction_id}`
    );
  };

  const isValidTipDetail =
    payment?.tips?.items?.some((tip) => tip?.tip_amount > 0) || false;

  return (
    <>
      <div className="flex mb-3">
        <span className="mr-auto">
          <Search
            queryParams={location.search}
            placeHolders="Search by Employee name, Employee ID and Ticket ID"
          />
        </span>
        {filterConfig && (
          <>
            <Filter config={filterConfig} queryParams={location.search} />
            <ExportBtn onClick={onExport} id="tips-export-btn" />
          </>
        )}
      </div>
      <Table
        data={payment.tips?.items || []}
        columns={tableColumns}
        onRowClick={handleRowClick}
        isEmpty={!isValidTipDetail}
        isLoading={Boolean(payment.isLoading)}
        defaultSortKey="transaction_date"
      />
      <Pagination
        pageId={queryParams}
        results={numResults}
        prevPage={prevPage}
        nextPage={nextPage}
        prevKey={prevKey}
        nextKey={nextKey}
      />
    </>
  );
};

export default TipsDetails;
